// Resources, permissions, scopes and the audience roles that bundle them: the
// target model of "Auger identity: Stytch + Hasura, no users table". Nothing
// enforces these yet; today's tokens carry users.class roles and the auger:read
// / crucible:read product scopes, which have no place here.
//
// The Stytch RBAC policy is the source of truth for roles and scopes; this is
// its typed mirror, so a server can answer "may this caller do X" from the
// token it already verified, with no SDK call. Change the policy and this file
// together.
//
// A resource is the thing acted on (a database, a mailbox, a repository),
// never a product that reaches it. Self-contained on purpose, like
// stytch-verify.ts: Crucible needs the same catalogue.

/** Each resource and the actions on it. An action exists only where its risk differs. */
export const RESOURCES = {
  "auger.records": ["read", "write", "delete"],
  "auger.schemas": ["write"],
  "google.mail": ["read", "draft", "send"],
  "google.calendar": ["read", "write"],
  // The Heavybit bot. A person's own Slack account becomes its own resource
  // (slack.user) when the first tool that acts as a person exists.
  "slack.heavybit": ["write"],
  harmonic: ["read"],
  clearbit: ["read"],
  pipedrive: ["read", "write"],
  // Submissions to Heavybit's review queues, and the founder-only skills.
  // Named without the vendor behind them: outsiders see these two on the
  // consent page and in Crucible's metadata (#1668).
  submissions: ["write"],
  "github.internal-skills": ["read"],
  "reserved-skills": ["read"],
} as const;

export type Resource = keyof typeof RESOURCES;
export type Permission = {
  [R in Resource]: `${R}:${(typeof RESOURCES)[R][number]}`;
}[Resource];

/** A Stytch scope is named after one permission. */
export type Scope = Permission;

export interface ScopeDefinition {
  /** The line the consent page shows beside the scope's checkbox. */
  description: string;
  /** What a token carrying the scope may do. Scopes on one resource accumulate. */
  permissions: readonly Permission[];
}

export const SCOPES = {
  "auger.records:read": {
    description: "Read your Auger records",
    permissions: ["auger.records:read"],
  },
  "auger.records:write": {
    description: "Change your Auger records",
    permissions: ["auger.records:read", "auger.records:write"],
  },
  "auger.records:delete": {
    description: "Permanently delete Auger data",
    permissions: ["auger.records:delete"],
  },
  "auger.schemas:write": {
    description: "Change the Auger data model",
    permissions: ["auger.schemas:write"],
  },
  "google.mail:read": {
    description: "Read your email",
    permissions: ["google.mail:read"],
  },
  "google.mail:draft": {
    description: "Draft emails for you",
    permissions: ["google.mail:read", "google.mail:draft"],
  },
  "google.mail:send": {
    description: "Send email as you",
    permissions: ["google.mail:read", "google.mail:draft", "google.mail:send"],
  },
  "google.calendar:read": {
    description: "Read your calendar",
    permissions: ["google.calendar:read"],
  },
  "google.calendar:write": {
    description: "Update your calendar",
    permissions: ["google.calendar:read", "google.calendar:write"],
  },
  "slack.heavybit:write": {
    description: "Act in Slack as Heavybit",
    permissions: ["slack.heavybit:write"],
  },
  "harmonic:read": {
    description: "Look up people and companies in Harmonic",
    permissions: ["harmonic:read"],
  },
  "clearbit:read": {
    description: "Look up people and companies in Clearbit",
    permissions: ["clearbit:read"],
  },
  "pipedrive:read": {
    description: "Read Pipedrive deals and contacts",
    permissions: ["pipedrive:read"],
  },
  "pipedrive:write": {
    description: "Change Pipedrive deals and contacts",
    permissions: ["pipedrive:read", "pipedrive:write"],
  },
  "submissions:write": {
    description: "Submit to Heavybit",
    permissions: ["submissions:write"],
  },
  "github.internal-skills:read": {
    description: "Use Heavybit internal skills",
    permissions: ["github.internal-skills:read"],
  },
  "reserved-skills:read": {
    description: "Use founder skills",
    permissions: ["reserved-skills:read"],
  },
} as const satisfies Record<Scope, ScopeDefinition>;

/**
 * The old names of renamed scopes, and the scope each now is. Stytch still
 * issues the old names (stytch-policy.ts keeps them in the policy), tokens
 * minted before the rename carry them, and clients cache metadata that names
 * them. permissionsOf reads an old name as its new scope, and the consent
 * page translates a request for one. The old names leave the policy, and this list, only when that is
 * decided (#1668).
 */
export const LEGACY_SCOPES = {
  "notion.submissions:write": "submissions:write",
  "github.reserved-skills:read": "reserved-skills:read",
} as const satisfies Record<string, Scope>;

/** The current scope a name stands for: itself, the scope an old name became, or undefined. */
export function currentScope(value: string): Scope | undefined {
  if (isScope(value)) return value;
  return Object.hasOwn(LEGACY_SCOPES, value)
    ? LEGACY_SCOPES[value as keyof typeof LEGACY_SCOPES]
    : undefined;
}

/**
 * The consent page lists scopes in this order, grouped by service. Within a
 * group: read, then draft or change, then delete or send, so the riskier line
 * always sits below the safer one. Every scope in SCOPES appears exactly once
 * (a test enforces it), so a new scope must be placed here before it can ship.
 */
export const CONSENT_GROUPS: ReadonlyArray<{
  label: string;
  scopes: readonly Scope[];
}> = [
  {
    label: "Auger",
    scopes: [
      "auger.records:read",
      "auger.records:write",
      "auger.records:delete",
      "auger.schemas:write",
    ],
  },
  {
    label: "Google",
    scopes: [
      "google.mail:read",
      "google.mail:draft",
      "google.mail:send",
      "google.calendar:read",
      "google.calendar:write",
    ],
  },
  { label: "Pipedrive", scopes: ["pipedrive:read", "pipedrive:write"] },
  { label: "Slack", scopes: ["slack.heavybit:write"] },
  { label: "Harmonic", scopes: ["harmonic:read"] },
  { label: "Clearbit", scopes: ["clearbit:read"] },
  {
    label: "Skills",
    scopes: ["github.internal-skills:read", "reserved-skills:read"],
  },
  { label: "Submissions", scopes: ["submissions:write"] },
];

/**
 * The scopes the consent page ticks before the person chooses: reading and
 * changing Auger records, which is what most agents are for. Everything else
 * (email, calendar, deletion, the data model, acting in Slack as Heavybit,
 * Pipedrive writes) starts unticked, so the person opts in on purpose.
 */
export const CONSENT_DEFAULT_SCOPES: readonly Scope[] = [
  "auger.records:read",
  "auger.records:write",
  // Crucible's submission tools (apply, post a job, request a skill). A
  // network member connecting Crucible would otherwise get none of them.
  "submissions:write",
];

const HEAVYBIT_STAFF = [
  "auger.records:read",
  "auger.records:write",
  "google.mail:read",
  "google.mail:draft",
  "google.mail:send",
  "google.calendar:read",
  "google.calendar:write",
  "harmonic:read",
  "clearbit:read",
  // Staff work the CRM: create_activity and the create_records mirror write
  // to Pipedrive, and nothing else would grant it.
  "pipedrive:read",
  "pipedrive:write",
  "github.internal-skills:read",
  // Staff curate the founder skills, so they see the reserved source too.
  "reserved-skills:read",
  "submissions:write",
] as const satisfies readonly Permission[];

const PORTFOLIO_MEMBER = [
  "auger.records:read",
  "submissions:write",
] as const satisfies readonly Permission[];

/**
 * Stytch roles, named by audience. A role's prefix matches the member's
 * organization. heavybit_admin and portfolio_founder spell out the role they
 * extend, so holding either alone is never less than its base.
 */
export const ROLE_PERMISSIONS = {
  heavybit_staff: HEAVYBIT_STAFF,
  heavybit_admin: [
    ...HEAVYBIT_STAFF,
    "auger.records:delete",
    "auger.schemas:write",
    "slack.heavybit:write",
  ],
  // A partner is always staff too (the heavybit.com email rule). The role
  // marks partners for the staff directory (the heavybit-partners list, kept
  // by workers/staffLists) and may carry partner-only access later. It is not
  // a Hasura role and never reaches a session.
  heavybit_partner: HEAVYBIT_STAFF,
  heavybit_agent: [
    "auger.records:read",
    "auger.records:write",
    "github.internal-skills:read",
  ],
  portfolio_member: PORTFOLIO_MEMBER,
  portfolio_founder: [...PORTFOLIO_MEMBER, "reserved-skills:read"],
  portfolio_agent: ["auger.records:read"],
  // No Auger data: the Auger MCP refuses non-staff, and Hasura has no rules
  // for network_member. Its one permission is Crucible's submission tier; the
  // reader tier comes from holding an audience role at all.
  network_member: ["submissions:write"],
} as const satisfies Record<string, readonly Permission[]>;

export type Role = keyof typeof ROLE_PERMISSIONS;

/** The description each role carries in the Stytch policy, where admins assign it. */
export const ROLE_DESCRIPTIONS = {
  heavybit_staff: "Heavybit staff",
  heavybit_admin:
    "Heavybit staff who may delete data, change the data model and act in Slack as Heavybit",
  heavybit_partner: "Heavybit partners",
  heavybit_agent: "Agents that work for Heavybit rather than for a person",
  portfolio_member: "Employees of a portfolio company",
  portfolio_founder: "Founders of a portfolio company",
  portfolio_agent: "Agents that work for a portfolio company",
  network_member: "Members of the Heavybit network",
} as const satisfies Record<Role, string>;

export function isScope(value: string): value is Scope {
  return Object.hasOwn(SCOPES, value);
}

export function isRole(value: string): value is Role {
  return Object.hasOwn(ROLE_PERMISSIONS, value);
}

/** The parts of a verified principal a permission check reads. */
export interface PermissionHolder {
  roles: readonly string[];
  scopes: readonly string[];
  /** Present on a Connected App access token, absent on a browser session JWT. */
  clientId?: string;
}

/**
 * What the caller may do. A browser session carries no scope claim and may do
 * everything its roles allow. A Connected App token may do what its approved
 * scopes allow AND its roles still allow: Stytch checks the roles only when it
 * issues a scope, so a role taken away after consent would otherwise live on
 * in the scope until the token expired. Unknown roles and scopes grant nothing.
 */
export function permissionsOf(holder: PermissionHolder): Set<Permission> {
  const byRole = new Set<Permission>();
  for (const role of holder.roles) {
    if (isRole(role))
      for (const permission of ROLE_PERMISSIONS[role]) byRole.add(permission);
  }
  if (!holder.clientId) return byRole;
  const granted = new Set<Permission>();
  for (const name of holder.scopes) {
    const scope = currentScope(name);
    if (!scope) continue;
    for (const permission of SCOPES[scope].permissions)
      if (byRole.has(permission)) granted.add(permission);
  }
  return granted;
}

export function can(holder: PermissionHolder, permission: Permission): boolean {
  return permissionsOf(holder).has(permission);
}

/**
 * The roles that make a caller Heavybit staff, for the gates that admit staff
 * only (the Auger MCP, /api/claude, the Netlify functions). These gates used
 * to read the `class` claim, which comes from users.class and goes away with
 * the users table; the roles come from Stytch.
 */
export const STAFF_ROLES = ["heavybit_staff", "heavybit_admin"] as const;

/** True when the caller holds a Heavybit staff role. */
export function isHeavybitStaff(holder: { roles: readonly string[] }): boolean {
  return holder.roles.some((role) =>
    (STAFF_ROLES as readonly string[]).includes(role),
  );
}
